import React from 'react';
import { isAxiosError } from 'axios';
import { SkeletonLoader } from '@plerionhq/design-system';
import type { AccessGrant } from '../../types';
import { useGetAssetRawData } from '../../api/use-get-asset-raw-data';
import {
  extractResourcePolicy,
  mechanismCarriesPolicyDocument,
} from '../../lib/resource-policy';
import { mechanismLabels } from '../../label-maps';
import { PolicyJsonViewer } from './policy-json-viewer';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-card border-container-border border border-dashed p-6 text-center text-xs text-gray-500 dark:text-gray-400">
      {message}
    </div>
  );
}

function describeError(t: TFunction<'accessGrants'>, error: unknown): string {
  if (isAxiosError(error)) {
    const apiMessage = error.response?.data?.message;
    if (typeof apiMessage === 'string' && apiMessage.length > 0) {
      return apiMessage;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return t('unknownError');
}

type AccessGrantPolicyTabProperties = {
  grant: AccessGrant;
};

export function AccessGrantPolicyTab({
  grant,
}: AccessGrantPolicyTabProperties) {
  const { t } = useTranslation('accessGrants');
  const hasPolicyDocument = mechanismCarriesPolicyDocument(grant.mechanism);
  const { data, status, error } = useGetAssetRawData(
    hasPolicyDocument ? grant.assetId : undefined,
  );

  const policyDocument = React.useMemo(() => {
    if (!hasPolicyDocument) {
      return null;
    }
    return extractResourcePolicy(grant.assetType, data?.data?.rawData?.data);
  }, [hasPolicyDocument, grant.assetType, data]);

  if (!hasPolicyDocument) {
    return (
      <EmptyState
        message={t('mechanismGrantsAreConfiguredOutsideThePolicy', {
          mechanism: mechanismLabels(t)[grant.mechanism],
        })}
      />
    );
  }

  if (status === 'pending') {
    return <SkeletonLoader status="loading" type="line" repeat={6} />;
  }

  if (status === 'error') {
    return (
      <EmptyState
        message={t('couldNotLoadResourcePolicy', {
          error: describeError(t, error),
        })}
      />
    );
  }

  if (policyDocument === null) {
    return (
      <EmptyState
        message={t('noPolicyDocumentWasFoundOnThis', {
          assetType: grant.assetType,
        })}
      />
    );
  }

  return (
    <div className="rounded-card border-container-border border">
      <PolicyJsonViewer document={policyDocument} />
    </div>
  );
}
